/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  Typography,
  FormControlLabel,
  FormControl,
  Collapse,
  RadioGroup,
  Radio,
  Divider,
} from '@material-ui/core';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { isEmpty, get } from 'lodash';
import { CheckCircle } from '@material-ui/icons';
import moment from 'moment';
import messages from 'containers/PreOpeningPage/messages';
import { translate } from 'containers/services/translate';
import { CLAIM } from 'containers/data-structure';
import { CHOICE } from 'containers/PreOpeningPage/constants';
import userPrivileges, {
  PRIVILEGES,
  userHasPrivilege,
} from 'containers/services/security/user-privileges';
import FraudeForm from 'containers/PreOpeningPage/Fraude/FraudeFormManager';
import { Listings } from 'containers/AppContainer/typings';
import { removeDeletedItems } from 'services/utils';
import { BUTTON_COLORS, COLORS } from 'constants/design';
import { ContainerFraude } from 'containers/PreOpeningPage/styled-component';
import {
  DrawerContainer,
  DrawerDiv,
} from 'containers/PreOpeningPage/styled-component';
import {
  formatUpdateFraudeInfo,
  validatorFraudInfo,
} from 'containers/PreOpeningPage/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { FraudInfoType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';

export interface FraudeDrawerProps {
  intl: IntlShape;
  claimInfo: any;
  listings?: Listings;
  claimRecap: ClaimRecapType;
  isDrawerOpen: boolean;
  setIsDrawerOpen: Function;
  requestUpdateFraudInfo?: Function;
  handleOpeningManagerUpdate: Function;
}

const FraudeDrawer: React.FC<FraudeDrawerProps> = (props): JSX.Element => {
  const [isSuspicious, setIsSuspicious] = useState(null);
  const [fraudInfo, setFraudInfo] = useState({});
  const [fraudMotifs, setFraudMotifs] = useState<Set<number>>([] as any);

  const [commentMotif, setCommentMotif] = useState<any>(null);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    valueField?: any,
  ) => {
    const { value } = event.target;

    const { claim } = props.claimInfo;
    switch (field) {
      case CLAIM.SUSPICIOUS:
        claim[CLAIM.SUSPICIOUS] = value;
        break;
      default:
        return props.claimInfo;
    }
    setIsSuspicious(value);
  };

  const isCourtManager = userPrivileges.userHasPrivilege(
    PRIVILEGES.ISCOURTMANAGER,
  );
  let value = props.claimInfo;

  const { claim } = value;

  useEffect(() => {
    setIsSuspicious(
      typeof get(claim, [CLAIM.SUSPICIOUS], '') === 'string'
        ? get(claim, [CLAIM.SUSPICIOUS], '')
        : get(claim, [CLAIM.SUSPICIOUS], '')
        ? CHOICE.YES
        : CHOICE.NO,
    );
    setFraudInfo(get(claim, 'fraudInfo[0]', {}));
  }, []);

  const handleToggleDrawer = () => {
    props.setIsDrawerOpen(!props.isDrawerOpen);
  };

  return (
    <DrawerContainer
      anchor="right"
      open={props.isDrawerOpen}
      onClose={handleToggleDrawer}
    >
      <DrawerDiv>
        <div className="titleConatainer">
          <span className="title">{'Fraude Informations'}</span>

          <ClearOutlinedIcon
            data-testid={'close-icon'}
            onClick={handleToggleDrawer}
          />
        </div>
        <Divider className="divider" />
        <div className="content">
          <ContainerFraude className="container">
            <div className="checkdiv">
              <Typography className="text">
                {translate({ ...messages.fraudeTitle }, props)}
              </Typography>

              <FormControl>
                <RadioGroup
                  row
                  onChange={handleChange(CLAIM.SUSPICIOUS)}
                  value={isSuspicious}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.YES}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate({ ...messages.yes }, props)}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.NO}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate({ ...messages.no }, props)}
                  />
                </RadioGroup>
              </FormControl>
            </div>

            <Collapse in={isSuspicious === CHOICE.YES}>
              <FraudeForm
                victims={removeDeletedItems(
                  get(props.claimInfo, 'victims', []),
                )}
                vehicles={removeDeletedItems(
                  get(props.claimInfo, 'vehicules', []),
                )}
                lossDate={moment(get(props.claimInfo, 'claim.lossDate')).format(
                  'DD/MM/YYYY',
                )}
                claimToPreopen={props.claimInfo}
                listings={props.listings}
                isManager={userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER)}
                fraudInfoValidation={{}}
                fullWidth
                isCourtManager={isCourtManager}
                setCommentMotif={setCommentMotif}
                commentMotif={commentMotif}
                setFraudMotifs={setFraudMotifs}
                fraudMotifs={fraudMotifs}
                fraudInfo={fraudInfo}
                setFraudInfo={setFraudInfo}
              />
            </Collapse>
          </ContainerFraude>
        </div>
        <div className="actions">
          <Divider className="divider" />
          <div className="action-buttons-container">
            <SecondaryButton
              className="cancelButton"
              onClick={handleToggleDrawer}
              title={translate({ ...messages.cancelBtn }, props)}
            />

            <PrimaryButton
              data-testid="primary-button"
              variant="contained"
              className={'saveButton'}
              disabled={
                isSuspicious === CHOICE.YES &&
                !isEmpty(fraudInfo) &&
                validatorFraudInfo(({
                  ...fraudInfo,
                  fraudMotifs: fraudMotifs,
                } as unknown) as FraudInfoType)
              }
              onClick={() =>
                props.handleOpeningManagerUpdate({
                  isSuspicious: isSuspicious === CHOICE.YES,
                  fraudInfo:
                    isSuspicious === CHOICE.YES
                      ? [
                          ...formatUpdateFraudeInfo(
                            ({
                              ...fraudInfo,
                              comment: commentMotif,
                            } as unknown) as FraudInfoType,
                            get(props.claimInfo, 'victims', []),
                            get(props.claimInfo, 'vehicules', []),
                            !isEmpty(
                              get(props.claimRecap, 'claim.fraudInfo[0]', {}),
                            )
                              ? {
                                  ...get(
                                    props.claimRecap,
                                    'claim.fraudInfo[0]',
                                    {},
                                  ),
                                  isDeleted: true,
                                  updatedBy: currentUserStorage.get()?.username,
                                }
                              : {},
                            false, //it's only courtmanager that could access to this drawer
                          ),
                        ]
                      : !isEmpty(
                          get(props.claimRecap, 'claim.fraudInfo[0]', {}),
                        )
                      ? [
                          {
                            ...get(props.claimRecap, 'claim.fraudInfo[0]', {}),
                            isDeleted: true,
                            updatedBy: currentUserStorage.get()?.username,
                          },
                        ]
                      : [],
                })
              }
              title={'Valider'}
              background={BUTTON_COLORS.PRIMARY_BLUE}
              border={BUTTON_COLORS.PRIMARY_BLUE}
              titlecolor={COLORS.WHITE}
            />
          </div>
        </div>
      </DrawerDiv>
    </DrawerContainer>
  );
};
export default injectIntl(FraudeDrawer);
